/*
 * Copyright (C) 2025 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import * as React from 'react'
import {useOutletContext, useNavigate} from 'react-router-dom'
import type {ToolDetailsOutletContext} from '../ToolDetails'
import {View, type ViewProps} from '@instructure/ui-view'
import {useScope as createI18nScope} from '@canvas/i18n'
import {Button} from '@instructure/ui-buttons'
import {Heading} from '@instructure/ui-heading'
import {Tooltip} from '@instructure/ui-tooltip'
import {IconCopyLine, IconRefreshLine} from '@instructure/ui-icons'
import {Text} from '@instructure/ui-text'
import {Flex} from '@instructure/ui-flex'
import {i18nLtiScope} from '@canvas/lti/model/i18nLtiScope'
import {i18nLtiPrivacyLevel} from '../../../model/i18nLtiPrivacyLevel'
import {i18nLtiPlacement} from '../../../model/i18nLtiPlacement'
import {DefaultLtiPrivacyLevel} from '../../../model/LtiPrivacyLevel'
import {isLtiPlacementWithIcon} from '../../../model/LtiPlacement'
import {filterPlacementObjectsByFeatureFlags} from '@canvas/lti/model/LtiPlacementFilter'
import {ToolConfigurationFooter} from './ToolConfigurationFooter'
import {showFlashAlert} from '@instructure/platform-alerts'
import {showConfirmationDialog} from '@canvas/dialogs/react/ConfirmationDialog'
import {
  useResetLtiRegistration,
  fetchLtiRegistrationWithLegacyConfig,
} from '../../../api/registrations'
import {isSuccessful} from '../../../../common/lib/apiResult/ApiResult'
import {PlacementInfoTooltip} from '../../../components/PlacementInfoTooltip'
import {MessageSetting} from '../../../model/internal_lti_configuration/InternalBaseLaunchSettings'
import {LtiPlacementlessMessageType} from '../../../model/LtiMessageType'
import {launchTypeSpecificSettingsLabels} from '../../../registration_wizard_forms/LaunchTypeSpecificSettingsConfirmation'
import {Section, SubSection} from '../../../components/Section'
import {LaunchSettingsReadOnlyView} from '../../../components/LaunchSettingsReadOnlyView'
import {IconUrlsReadOnlyView} from '../../../components/IconUrlsReadOnlyView'
import {extractSubstitutionVariables} from '../../../lib/extractSubstitutionVariables'
import {CustomVariablesList} from '../../../components/CustomVariablesList'

const I18n = createI18nScope('lti_registrations')

const LaunchTypeSpecificSettingsSection = (
  settings: MessageSetting[],
  type: LtiPlacementlessMessageType,
) => {
  const setting = settings.find(s => s.type === type)
  if (!setting) return null

  const typeLabels = launchTypeSpecificSettingsLabels[type as LtiPlacementlessMessageType]

  const customFields = Object.entries(setting.custom_fields || {})
  return (
    <Section title={typeLabels.title} margin="0 small medium small">
      <SubSection title={typeLabels.enableLabel}>
        <Text size="small">{setting?.enabled ? I18n.t('Yes') : I18n.t('No')}</Text>
      </SubSection>
      {setting.target_link_uri && (
        <SubSection title={typeLabels.targetLinkUriLabel}>
          <Text>{setting.target_link_uri}</Text>
        </SubSection>
      )}
      <SubSection title={typeLabels.customFieldsLabel}>
        {customFields.length === 0 ? (
          <Text fontStyle="italic">{I18n.t('No custom fields configured.')}</Text>
        ) : (
          <View as="div" margin="x-small 0 0 0">
            <pre style={{fontFamily: 'monospace'}}>
              {customFields.map(([key, field]) => `${key}=${field}`).join('\n')}
            </pre>
          </View>
        )}
      </SubSection>
    </Section>
  )
}

export const ToolConfigurationView = () => {
  const {registration} = useOutletContext<ToolDetailsOutletContext>()
  const mutation = useResetLtiRegistration()
  const navigate = useNavigate()

  const redirectUris = registration.overlaid_configuration.redirect_uris || []
  const enabledPlacements = filterPlacementObjectsByFeatureFlags(
    registration.overlaid_configuration.placements.filter(p => {
      return !('enabled' in p) || p.enabled
    }),
  )

  const enabledPlacementsWithIcons = enabledPlacements.filter(p =>
    isLtiPlacementWithIcon(p.placement),
  )

  const enabledMessageSettings = (
    registration.overlaid_configuration.launch_settings?.message_settings || []
  ).filter(setting => setting.enabled)

  const [tooltipShowing, setTooltipShowing] = React.useState(false)
  const [editTooltipShowing, setEditTooltipShowing] = React.useState(false)

  // TEMPORARY: This is a temporary change. We'll revert this once we disable the old developer keys page.
  // Manual registrations no longer use overlays (they edit the base config directly),
  // so "Restore Default" doesn't make sense for them. Only show it for dynamic registrations.
  const isManualRegistration = registration.manual_configuration_id !== null
  const isInherited = registration.inherited ?? false
  const canRestoreDefault = !isInherited && !isManualRegistration

  // Edit as JSON should only be available for manual LTI Registrations,
  // not Lti::IMS::Registration, and not Lti::Registrations that have a template_registration_id
  const canEditAsJson =
    window.ENV.LTI_EDIT_JSON &&
    isManualRegistration &&
    registration.ims_registration_id === null &&
    !registration.template_registration_id &&
    !isInherited

  const handleRestoreDefault = React.useCallback(
    async (e: React.KeyboardEvent<ViewProps> | React.MouseEvent<ViewProps, MouseEvent>) => {
      e.preventDefault()
      const confirmed = await showConfirmationDialog({
        body: I18n.t(
          'Are you sure you want to reset this app’s settings to their default values? Once they are reverted the action cannot be undone.',
        ),
        confirmColor: 'danger',
        confirmText: I18n.t('Reset'),
        label: I18n.t('Reset App Configuration'),
        size: 'small',
      })

      if (confirmed) {
        await mutation.mutateAsync({
          ltiRegistrationId: registration.id,
          accountId: registration.account_id,
        })
      }
    },
    [mutation, registration.account_id, registration.id],
  )

  const handleCopyJsonConfig = React.useCallback(
    async (e: React.KeyboardEvent<ViewProps> | React.MouseEvent<ViewProps, MouseEvent>) => {
      e.preventDefault()
      const legacyConfigResponse = await fetchLtiRegistrationWithLegacyConfig(
        registration.account_id,
        registration.id,
      )

      if (isSuccessful(legacyConfigResponse)) {
        const legacyConfig = legacyConfigResponse.data['overlaid_legacy_configuration']
        try {
          await navigator.clipboard.writeText(JSON.stringify(legacyConfig, null, 2))
          showFlashAlert({
            type: 'info',
            message: I18n.t('JSON configuration copied'),
          })
        } catch {
          showFlashAlert({
            type: 'error',
            message: I18n.t('Unable to copy JSON code to clipboard'),
          })
        }
      } else {
        showFlashAlert({
          type: 'error',
          message: I18n.t('Unable to get JSON configuration to be copied.'),
        })
      }
    },
    [registration],
  )

  return (
    <div>
      {registration.manual_configuration_id ? (
        <Section title={I18n.t('Launch Settings')}>
          <LaunchSettingsReadOnlyView
            redirectUris={redirectUris}
            targetLinkUri={registration.overlaid_configuration.target_link_uri}
            oidcInitiationUrl={registration.overlaid_configuration.oidc_initiation_url}
            publicJwkUrl={registration.overlaid_configuration.public_jwk_url}
            publicJwk={registration.overlaid_configuration.public_jwk}
            domain={registration.overlaid_configuration.domain}
            customFields={registration.overlaid_configuration.custom_fields}
          />
        </Section>
      ) : null}

      <Section title={I18n.t('Permissions')}>
        <Flex direction="column" data-testid="permissions" gap="xx-small">
          {registration.overlaid_configuration.scopes.length === 0 ? (
            <Text fontStyle="italic">{I18n.t('This app has no permissions configured.')}</Text>
          ) : (
            registration.overlaid_configuration.scopes.map(scope => (
              <Flex.Item key={scope}>
                <Text as="div">{i18nLtiScope(scope)}</Text>
              </Flex.Item>
            ))
          )}
        </Flex>
      </Section>

      <Section title={I18n.t('Data Sharing')}>
        <SubSection title={I18n.t('Privacy Level')}>
          <Text>
            {i18nLtiPrivacyLevel(
              registration.overlaid_configuration.privacy_level || DefaultLtiPrivacyLevel,
            )}
          </Text>
        </SubSection>
        <CustomVariablesList internalConfiguration={registration.overlaid_configuration} />
      </Section>

      <Section title={I18n.t('Placements')}>
        <Flex direction="column" gap="xx-small">
          {enabledPlacements.length === 0 ? (
            <Text fontStyle="italic">{I18n.t('No placements enabled.')}</Text>
          ) : (
            enabledPlacements.map((p, i) => (
              <Flex.Item key={p.placement}>
                <Flex gap="x-small">
                  <Flex.Item>
                    <Text key={i}>{i18nLtiPlacement(p.placement)}</Text>
                  </Flex.Item>
                  <Flex.Item>
                    <PlacementInfoTooltip placement={p.placement} />
                  </Flex.Item>
                </Flex>
              </Flex.Item>
            ))
          )}
        </Flex>
      </Section>

      {LaunchTypeSpecificSettingsSection(enabledMessageSettings, 'LtiEulaRequest')}

      <Section title={I18n.t('Administration Nickname and Description')}>
        <Flex direction="row" alignItems="center" margin="small 0 0">
          <Flex.Item margin="0 xx-small 0 0">
            <Text weight="bold">{I18n.t('Administration Nickname:')}</Text>
          </Flex.Item>
          <Flex.Item shouldShrink>
            {registration.admin_nickname ? (
              <Text wrap="break-word">{registration.admin_nickname}</Text>
            ) : (
              <Text fontStyle="italic">{I18n.t('No nickname')}</Text>
            )}
          </Flex.Item>
        </Flex>

        <Flex direction="column" alignItems="start" margin="small 0 0">
          <Flex.Item margin="0 xx-small 0 0">
            <Text weight="bold">{I18n.t('Description:')}</Text>
          </Flex.Item>
          <Flex.Item shouldShrink>
            {registration.overlaid_configuration.description ? (
              <Text wrap="break-word">{registration.overlaid_configuration.description}</Text>
            ) : (
              <Text fontStyle="italic">{I18n.t('No description')}</Text>
            )}
          </Flex.Item>
        </Flex>

        <Heading level="h3" margin="small 0" id="placements">
          {I18n.t('Placement Names')}
        </Heading>
        {enabledPlacements.map((p, i) => (
          <Flex direction="row" alignItems="center" margin="small 0 0" key={i} gap="xx-small">
            <Flex.Item>
              <Text weight="bold">{i18nLtiPlacement(p.placement)}:</Text>
            </Flex.Item>
            <Flex.Item shouldShrink>
              {p.text ? (
                <Text wrap="break-word">{p.text}</Text>
              ) : (
                <Text fontStyle="italic">{I18n.t('No text')}</Text>
              )}
            </Flex.Item>
          </Flex>
        ))}
      </Section>

      <Section title={I18n.t('Tool Icon URL')}>
        <IconUrlsReadOnlyView
          toolIconUrl={registration.overlaid_configuration.launch_settings?.icon_url}
          placements={enabledPlacementsWithIcons}
          registrationName={registration.name}
          developerKeyId={registration.developer_key_id}
        />
      </Section>

      <ToolConfigurationFooter>
        <Flex direction="row" justifyItems="space-between" padding="0 small">
          <Flex.Item>
            <Flex gap="small">
              {/* TEMPORARY: Hide Restore Default button for manual registrations */}
              {!isManualRegistration ? (
                <Flex.Item>
                  <Tooltip
                    renderTip={I18n.t(
                      "This account does not own this app and therefore can't reset its configuration.",
                    )}
                    isShowingContent={tooltipShowing}
                    onShowContent={() => {
                      // The tooltip should only be shown if they *can't* click the restore default button
                      setTooltipShowing(!canRestoreDefault)
                    }}
                    onHideContent={() => {
                      setTooltipShowing(false)
                    }}
                  >
                    <Button
                      data-pendo="lti-registrations-restore-default"
                      color="primary-inverse"
                      interaction={canRestoreDefault ? 'enabled' : 'disabled'}
                      renderIcon={<IconRefreshLine />}
                      margin="0"
                      onClick={handleRestoreDefault}
                    >
                      {I18n.t('Restore Default')}
                    </Button>
                  </Tooltip>
                </Flex.Item>
              ) : null}
              {registration.ims_registration_id === null ? (
                <Flex.Item>
                  <Button
                    color="primary-inverse"
                    renderIcon={<IconCopyLine />}
                    margin="0"
                    onClick={handleCopyJsonConfig}
                  >
                    {I18n.t('Copy JSON Code')}
                  </Button>
                </Flex.Item>
              ) : null}
            </Flex>
          </Flex.Item>

          <Flex.Item>
            <Flex gap="small">
              <Flex.Item>
                {canEditAsJson && (
                  <Flex.Item>
                    <Button
                      data-pendo="lti-registrations-edit-json"
                      color="secondary"
                      onClick={_ => {
                        navigate(`/manage/${registration.id}/configuration/edit-json`)
                      }}
                    >
                      {I18n.t('Edit as JSON')}
                    </Button>
                  </Flex.Item>
                )}
              </Flex.Item>
              <Flex.Item>
                <Tooltip
                  renderTip={I18n.t(
                    "This account does not own this app and therefore can't edit its configuration.",
                  )}
                  isShowingContent={editTooltipShowing}
                  onShowContent={() => {
                    // The tooltip should only be shown if they *can't* click the edit button
                    setEditTooltipShowing(isInherited)
                  }}
                  onHideContent={() => {
                    setEditTooltipShowing(false)
                  }}
                >
                  <Button
                    data-pendo="lti-registrations-edit-config"
                    color="primary"
                    interaction={isInherited ? 'disabled' : 'enabled'}
                    onClick={_ => {
                      navigate(`/manage/${registration.id}/configuration/edit`)
                    }}
                  >
                    {I18n.t('Edit')}
                  </Button>
                </Tooltip>
              </Flex.Item>
            </Flex>
          </Flex.Item>
        </Flex>
      </ToolConfigurationFooter>
    </div>
  )
}
